import React from 'react';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import api from '../../../shared/utils/api';
import { ControlReceiptRecord, CustomerOption, NewCloseData } from './types';
import { formatNumber, PERIOD_TYPE_OPTIONS, YEAR_OPTIONS } from './utils';

interface Step1ControlReceiptProps {
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  setNewCloseData: (data: NewCloseData) => void;
  controlReceiptData: ControlReceiptRecord[];
  loadingControlReceipt: boolean;
  syncingReceipts: boolean;
  setSyncingReceipts: (value: boolean) => void;
  fetchControlReceipt: (customerOid: string, yearType: string, periodType?: string) => Promise<void>;
  setErrorMessage: (message: string) => void;
  setShowErrorPopup: (show: boolean) => void;
  setWarningMessage: (message: string) => void;
  setShowWarningAlert: (show: boolean) => void;
  onCancel?: () => void;
  onContinue?: () => void;
  loading?: boolean;
}

const Step1ControlReceipt: React.FC<Step1ControlReceiptProps> = ({
  customers,
  newCloseData,
  setNewCloseData,
  controlReceiptData,
  loadingControlReceipt,
  syncingReceipts,
  setSyncingReceipts,
  fetchControlReceipt,
  setErrorMessage,
  setShowErrorPopup,
  setWarningMessage,
  setShowWarningAlert,
  onCancel,
  onContinue,
  loading = false,
}) => {
  const customerOptions = customers.map(customer => ({
    value: customer.Oid,
    label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
  }));

  const handleSyncReceipts = async () => {
    if (syncingReceipts) {
      return;
    }
    if (!newCloseData.CustomerOid || !newCloseData.YearType) {
      setErrorMessage('Харилцагч болон жил сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }

    try {
      setSyncingReceipts(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: newCloseData.CustomerOid,
        yearType: newCloseData.YearType
      });
      
      const maxAttempts = 30;
      const retryDelayMs = 3000;
      let attempt = 0;

      const pollUntilUnlocked = async () => {
        while (attempt < maxAttempts) {
          attempt += 1;
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
          const statusResult = await api.get(`/api/cron/receipts/sync/status?${queryParams.toString()}`);
          if (!statusResult?.running) {
            return true;
          }
        }
        return false;
      };

      const result = await api.post(`/api/cron/receipts/sync?${queryParams.toString()}`, {});
      const isLocked = result?.success === false
        && (result?.locked || result?.lockKey || result?.message?.includes('аль хэдийн ажиллаж'));

      if (isLocked) {
        const unlocked = await pollUntilUnlocked();
        if (!unlocked) {
          throw new Error('Бичилтүүд синк дуусаагүй байна. Дараа дахин оролдоно уу.');
        }
        await fetchControlReceipt(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
        setWarningMessage('Синк дууссаны дараа мэдээллийг шинэчиллээ');
        setShowWarningAlert(true);
        return;
      }

      if (result && result.success) {
        setWarningMessage('Бичилтүүд амжилттай синк хийгдлээ');
        setShowWarningAlert(true);
        
        // Refresh control receipt data after sync
        await fetchControlReceipt(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
        return;
      }

      throw new Error(result?.message || 'Бичилтүүд синк хийхэд алдаа гарлаа');
    } catch (error: any) {
      console.error('Error syncing receipts:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Бичилтүүд синк хийхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setSyncingReceipts(false);
    }
  };

  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Харилцагч <span className="text-red-500">*</span>
          </label>
        </div>
        <div className="flex gap-3 ml-4">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Цуцлах
            </button>
          )}
          {onContinue && (
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={loading || loadingControlReceipt}
            >
              {loading || loadingControlReceipt ? 'Ажиллаж байна...' : 'Үргэлжлүүлэх'}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <SearchableDropdown
            options={customerOptions}
            value={newCloseData.CustomerOid}
            onChange={(value) => setNewCloseData({ ...newCloseData, CustomerOid: value })}
            valueLabel={customers.find(c => c.Oid === newCloseData.CustomerOid) 
              ? `${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerID || ''})`
              : ''}
            placeholder="Харилцагч сонгох"
            className="w-full"
            enableAutoComplete={true}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Жил <span className="text-red-500">*</span>
            </label>
            <select
              value={newCloseData.YearType}
              onChange={(e) => setNewCloseData({ ...newCloseData, YearType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {YEAR_OPTIONS.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Хугацаа
            </label>
            <select
              value={newCloseData.PeriodType}
              onChange={(e) => setNewCloseData({ ...newCloseData, PeriodType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {PERIOD_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Control Receipt Section */}
      <div className="mt-6 border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Бичилтийн хяналтын тайлан</h3>
          <button
            onClick={handleSyncReceipts}
            disabled={syncingReceipts || loadingControlReceipt}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {syncingReceipts ? (
              <>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Уншиж байна...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Баримт татах
              </>
            )}
          </button>
        </div>
        
        {loadingControlReceipt ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Бичилтийн хяналтын тайлан уншиж байна...</p>
          </div>
        ) : controlReceiptData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Сар</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Бичилтийн тоо</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Хотын татвар (борлуулалт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-тай борлуулалт</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй борлуулалт</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-тай борлуулалт (нийт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Хотын татвар (худалдан авалт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-тай худалдан авалт</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй худалдан авалт</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-тай худалдан авалт (нийт)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {controlReceiptData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{row.PeriodType}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{row.ReceiptCount}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumCityTaxSales)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumVATPayable)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumNonVATSales)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumVATInclusiveSales)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CityTaxPurchaseAmount)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumVATReceivable)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumNonVATPurchase)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.SumVATInclusivePurchase)}</td>
                  </tr>
                ))}
                {/* Total Row */}
                {controlReceiptData.length > 0 && (() => {
                  const totals = controlReceiptData.reduce((acc, row) => ({
                    ReceiptCount: (acc.ReceiptCount || 0) + (row.ReceiptCount || 0),
                    SumCityTaxSales: (acc.SumCityTaxSales || 0) + (row.SumCityTaxSales || 0),
                    SumVATPayable: (acc.SumVATPayable || 0) + (row.SumVATPayable || 0),
                    SumNonVATSales: (acc.SumNonVATSales || 0) + (row.SumNonVATSales || 0),
                    SumVATInclusiveSales: (acc.SumVATInclusiveSales || 0) + (row.SumVATInclusiveSales || 0),
                    CityTaxPurchaseAmount: (acc.CityTaxPurchaseAmount || 0) + (row.CityTaxPurchaseAmount || 0),
                    SumVATReceivable: (acc.SumVATReceivable || 0) + (row.SumVATReceivable || 0),
                    SumNonVATPurchase: (acc.SumNonVATPurchase || 0) + (row.SumNonVATPurchase || 0),
                    SumVATInclusivePurchase: (acc.SumVATInclusivePurchase || 0) + (row.SumVATInclusivePurchase || 0)
                  }), {
                    ReceiptCount: 0,
                    SumCityTaxSales: 0,
                    SumVATPayable: 0,
                    SumNonVATSales: 0,
                    SumVATInclusiveSales: 0,
                    CityTaxPurchaseAmount: 0,
                    SumVATReceivable: 0,
                    SumNonVATPurchase: 0,
                    SumVATInclusivePurchase: 0
                  });
                  
                  return (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-2 py-2 whitespace-nowrap text-gray-900 border border-gray-300">Нийт</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{totals.ReceiptCount}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumCityTaxSales)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumVATPayable)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumNonVATSales)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumVATInclusiveSales)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.CityTaxPurchaseAmount)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumVATReceivable)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumNonVATPurchase)}</td>
                      <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(totals.SumVATInclusivePurchase)}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            Бичилтийн хяналтын тайлангийн мэдээлэл олдсонгүй
          </div>
        )}
      </div>
    </>
  );
};

export default Step1ControlReceipt;
